import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, MenuItem, Select, FormControl, InputLabel,
  Chip, Alert, Paper,
} from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import SEOHead from '../components/SEO/SEOHead';
import BlogService from '../services/blog-service';
import { ArticleCategory } from '../types/blog';

const CATEGORIES: ArticleCategory[] = [
  'Styling Tips', 'Trend Roundups', 'How to Style',
  'Fashion Advice', 'Seasonal Fashion', 'Street Style',
];

const MIN_WORDS = 500;

const ArticleEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<ArticleCategory>('Styling Tips');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [featuredImageUrl, setFeaturedImageUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Placeholder.configure({ placeholder: 'Start writing your article (minimum 500 words)…' }),
    ],
    content: '',
  });

  const getWordCount = useCallback(() => {
    if (!editor) return 0;
    const text = editor.getText();
    return text.trim().split(/\s+/).filter(Boolean).length;
  }, [editor]);

  const handleAddTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setTagInput('');
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!editor) return;
    const wordCount = getWordCount();
    if (wordCount < MIN_WORDS) { setError(`Article must contain at least ${MIN_WORDS} words. Current: ${wordCount}.`); return; }

    setSubmitting(true);
    try {
      const article = await BlogService.createArticle({
        title: title.trim(),
        body: editor.getHTML(),
        category,
        tags,
        featuredImageUrl,
      });
      
      if (article.status === 'pending_review') {
        setSuccess('Your article has been submitted and is pending review by our moderation team. You\'ll be notified once it\'s approved.');
      } else if (article.status === 'rejected') {
        setError(`Your article was flagged by our content safety system: ${article.rejectionReason || 'Content does not meet our guidelines.'}. Please revise and resubmit.`);
      } else {
        navigate(`/blog/${article.slug}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to create article.');
    } finally {
      setSubmitting(false);
    }
  };

  const wordCount = getWordCount();

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <SEOHead title="Write Article" description="Create a new fashion article on Hooliah." canonicalUrl="https://hooliah.com/blog/new" />

      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>Write Article</Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        All articles are reviewed for quality and safety before publishing. Articles must be fashion-related and appropriate for all ages. Your article will be published after approval by our moderation team.
      </Alert>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ mb: 2 }} />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Category</InputLabel>
        <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value as ArticleCategory)}>
          {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
      </FormControl>

      {/* Tags */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth label="Tags" placeholder="Type and press Enter"
          value={tagInput} onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleTagKeyDown} onBlur={handleAddTag} size="small"
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {tags.map((t) => <Chip key={t} label={t} size="small" onDelete={() => setTags(tags.filter((x) => x !== t))} />)}
        </Box>
      </Box>

      <TextField fullWidth label="Featured Image URL" value={featuredImageUrl}
        onChange={(e) => setFeaturedImageUrl(e.target.value)} sx={{ mb: 3 }} size="small"
      />

      {/* TipTap Editor */}
      <Paper variant="outlined" sx={{ p: 2, mb: 1, minHeight: 300, '& .ProseMirror': { minHeight: 260, outline: 'none' } }}>
        <EditorContent editor={editor} />
      </Paper>
      <Typography variant="caption" color={wordCount >= MIN_WORDS ? 'success.main' : 'text.secondary'} sx={{ mb: 3, display: 'block' }}>
        {wordCount} / {MIN_WORDS} words {wordCount >= MIN_WORDS ? '✓' : ''}
      </Typography>

      <Button variant="contained" size="large" onClick={handleSubmit} disabled={submitting || !!success}>
        {submitting ? 'Submitting…' : 'Submit for Review'}
      </Button>
    </Box>
  );
};

export default ArticleEditorPage;
